// What Remote Relay adds to subjects (web/src/lib/subject.ts, whose kinds,
// names, keys and facets these build on; docs/superpowers/specs/2026-11-01-subjects-design.md):
// keys read back from the wire, the data categories a grant on each kind
// may hold and the category each tool reads (both from the kind's Facets),
// and which subjects this Changeling grants. A grant, a query and a task
// name a subject by its key (subject.ts `subjectKey`), and only a resolved
// subject has one: a ref locator (`RefLocator`) can reach none of them.
// The plugin's plugins/changeling/scripts/subject.mjs holds the same rules,
// and the wire contract's `subjects` (testdata/contract.json,
// relaySubject.test.ts, with the router's parser) holds both to them.
import { canonicalSubject, compareHead, facetsOf, isGitHubName, isRefName, isRefPath, subjectKey as keyOf, type Facets, type RefLocator, type Subject, type SubjectKind } from '../subject';
export type { Subject, SubjectKind };

declare const KEY: unique symbol;
/** A subject's key, as `subjectKey` writes it and `readKey` accepts it: never any other string. */
export type SubjectKey = string & { readonly [KEY]: true };

export const KINDS: readonly SubjectKind[] = ['pull', 'compare', 'ref'];
/** What the person and the agent call each kind. */
export const KIND_NAMES: Readonly<Record<SubjectKind, string>> = { pull: 'pull request', compare: 'compare', ref: 'branch' };

export type RelayCategory = 'metadata' | 'comments' | 'files' | 'diff' | 'checks' | 'history';
export const RELAY_CATEGORIES: readonly RelayCategory[] = ['metadata', 'comments', 'files', 'diff', 'checks', 'history'];
/**
 * Whether a subject with `f` has what `c` reads: its description and
 * state always; comments with GitHub's conversation; files and the diff
 * with its range views; checks on a commit; history (a branch's commits,
 * the pull requests that brought them in, its force-pushes) where its
 * commits' linked pull requests are a facet.
 */
const has = (f: Facets, c: RelayCategory): boolean =>
  c === 'metadata' ? true : c === 'comments' ? f.conversation : c === 'files' || c === 'diff' ? f.range : c === 'checks' ? f.checks : f.linkedPulls;
/** The categories a grant on a subject of `kind` may hold. */
export const categoriesFor = (kind: SubjectKind): readonly RelayCategory[] => RELAY_CATEGORIES.filter((c) => has(facetsOf(kind), c));
/** What an approval starts with: every category the kind allows but history, which can reach far into the repository and is never preset. */
export const presetFor = (kind: SubjectKind): RelayCategory[] => categoriesFor(kind).filter((c) => c !== 'history');

export type RelayTool = 'view' | 'comments' | 'files' | 'diff' | 'commits' | 'checks';
export const RELAY_TOOLS: readonly RelayTool[] = ['view', 'comments', 'files', 'diff', 'commits', 'checks'];
export const isTool = (tool: unknown): tool is RelayTool => typeof tool === 'string' && (RELAY_TOOLS as readonly string[]).includes(tool);
/**
 * The category `tool` reads on a subject of `kind`, or null where that kind
 * has nothing for it to read: commits read a pull request's metadata, and
 * the history of a subject whose history is a facet.
 */
export function toolCategory(tool: RelayTool, kind: SubjectKind): RelayCategory | null {
  const f = facetsOf(kind);
  const c: RelayCategory = tool === 'view' ? 'metadata' : tool === 'commits' ? (f.linkedPulls ? 'history' : 'metadata') : tool;
  return has(f, c) ? c : null;
}
/** Why `tool` reads nothing of a subject of `kind`, naming the kind. */
export const noFacet = (tool: RelayTool, kind: SubjectKind) => `A ${KIND_NAMES[kind]} has no ${tool}: ${tool} reads pull requests only.`;

/**
 * A history's locator on the wire, `owner/repo/<spec>` (what a
 * `/gh/o/r/commits/<spec>` URL names before the repository says where its
 * ref ends, subject.ts `RefLocator`), in a field of its own (`locator`),
 * never where a key goes: an agent may connect to one, and this browser
 * resolves it (state/ref/fetch.ts `resolveRef`) and answers with the key.
 * Null when `text` is none: its spec's components are not empty, and its
 * first is a name git takes or `HEAD`.
 */
export function parseLocator(text: unknown): RefLocator | null {
  if (typeof text !== 'string' || text.length > 2048 || /[\x00-\x1f\x7f]/.test(text)) return null;
  const [owner, repo, ...spec] = text.split('/');
  if (!isGitHubName(owner) || !isGitHubName(repo) || !spec.length || spec.some((c) => c === '') || (spec[0] !== 'HEAD' && !isRefName(spec[0]))) return null;
  return { owner, repo, spec: spec.join('/') };
}
/** A locator's wire text. */
export const locatorText = (l: RefLocator): string => `${l.owner}/${l.repo}/${l.spec}`;

const SHA_LIKE = /^[0-9a-fA-F]{7,40}$/;
const FULL_SHA = /^(?:[0-9a-f]{40}|[0-9a-f]{64})$/;
/**
 * Whether `key` can be what `l` resolves to: a ref of its repository whose
 * name and path, joined, are its spec (`HEAD`, the default branch: any ref,
 * the rest its path; an abbreviated SHA: the full SHA). The agent's
 * computer holds the browser's answer to the same rule
 * (plugins/changeling/scripts/subject.mjs `resolvesTo`), so a link or a
 * request for one history is never granted another.
 */
export function resolvesTo(l: RefLocator, key: string): boolean {
  const s = parseSubject(key);
  if (s?.kind !== 'ref' || s.owner !== l.owner.toLowerCase() || s.repo !== l.repo.toLowerCase()) return false;
  const [first, ...rest] = l.spec.split('/');
  const path = rest.length ? rest.join('/') : null;
  if (s.ref + (s.path === null ? '' : '/' + s.path) === l.spec) return true;
  if (first === 'HEAD') return s.path === path;
  return SHA_LIKE.test(first) && FULL_SHA.test(s.ref) && s.ref.startsWith(first.toLowerCase()) && s.path === path;
}

/** A subject's key (subject.ts `subjectKey`). */
export const subjectKey = (s: Subject): SubjectKey => keyOf(s) as SubjectKey;
/** The key of pull request `number` of `owner/repo`. */
export const pullKey = (owner: string, repo: string, number: number): SubjectKey => subjectKey({ kind: 'pull', owner, repo, number });

/** A compare's head as GitHub writes it (`feat`, `ana:feat`, `ana:fork:feat`), or null when it names none. */
function headOf(text: string): Pick<Extract<Subject, { kind: 'compare' }>, 'head' | 'headRepo'> | null {
  const parts = text.split(':');
  const head = parts[parts.length - 1];
  if (parts.length > 3 || !isRefName(head)) return null;
  if (parts.length === 1) return { head, headRepo: null };
  const [owner, repo] = parts;
  if (!isGitHubName(owner) || (parts.length === 3 && !isGitHubName(repo))) return null;
  return { head, headRepo: { owner, repo: parts.length === 3 ? repo : null } };
}
const KEY_SHAPE = /^([^/\s#@:]+)\/([^/\s#@:]+)([#@:])(.+)$/;
/**
 * The subject a key names, in its canonical spelling (`canonicalSubject`:
 * a key spelled with other cases of owner and repository names, or a head
 * in this repository written with its owner, names the same subject);
 * null when it names none. Every name in it must be one subject.ts takes
 * (`isGitHubName`, `isRefName`, `isRefPath`), and the key's grammar is the
 * canonical one (`...`, never `..`).
 */
export function parseSubject(key: unknown): Subject | null {
  const m = typeof key === 'string' && key.length <= 2048 ? KEY_SHAPE.exec(key) : null;
  if (!m) return null;
  const [, owner, repo, sep, rest] = m;
  if (!isGitHubName(owner) || !isGitHubName(repo)) return null;
  let s: Subject | null = null;
  if (sep === '#') {
    const number = Number(rest);
    s = /^[1-9]\d{0,9}$/.test(rest) && number <= 2 ** 31 ? { kind: 'pull', owner, repo, number } : null;
  } else if (sep === '@') {
    const three = rest.indexOf('...');
    const base = three < 0 ? null : rest.slice(0, three);
    const head = headOf(three < 0 ? rest : rest.slice(three + 3));
    s = head && (base === null || isRefName(base)) ? { kind: 'compare', owner, repo, base, ...head } : null;
  } else {
    const cut = rest.indexOf('//');
    const ref = cut < 0 ? rest : rest.slice(0, cut), path = cut < 0 ? null : rest.slice(cut + 2);
    s = isRefName(ref) && (path === null || isRefPath(path)) ? { kind: 'ref', owner, repo, ref, path } : null;
  }
  // The key's grammar, names as written: what it says is all it says.
  return s && spelled(s) === key ? canonicalSubject(s) : null;
}
/** A subject written out with its names as they are (`subjectKey` without the canonical spelling). */
function spelled(s: Subject): string {
  const repo = `${s.owner}/${s.repo}`;
  if (s.kind === 'pull') return `${repo}#${s.number}`;
  if (s.kind === 'compare') return s.base === null ? `${repo}@${compareHead(s)}` : `${repo}@${s.base}...${compareHead(s)}`;
  return `${repo}:${s.ref}${s.path === null ? '' : '//' + s.path}`;
}
/** The key `v` names, in its canonical spelling, when it names one. */
export function readKey(v: unknown): SubjectKey | null {
  const s = parseSubject(v);
  return s ? (keyOf(s) as SubjectKey) : null;
}
/** A key's canonical spelling: the subject's one key (`subjectKey`), which every spelling of it shares. */
export function matchKey(key: SubjectKey): string {
  return keyOf(parseSubject(key)!);
}
/** Whether `a` and `b` name one subject. */
export const sameSubject = (a: SubjectKey, b: SubjectKey): boolean => matchKey(a) === matchKey(b);
